// Pure mapping between a saved cost-share schedule and the edit-form fields.
// Kept free of Angular/DOM so it can be unit-tested directly (see the sibling
// *.test.js). Used by the patient cost-share controller to pre-fill the
// schedule modal with the patient's existing values when updating.
//
// Field TYPES matter here: the modal binds `amountDollars` to <input
// type="number"> and `startDate` to <input type="date">. AngularJS only renders
// a Number into a number input and a Date into a date input — a string leaves
// the field blank. So the amount is a Number and the start date is a Date.

// Blank defaults for setting up a schedule for the first time (no schedule yet).
// null (not "") so the number/date inputs render empty rather than invalid.
export const DEFAULT_SCHEDULE_FORM = {
  amountDollars: null,
  frequency: "monthly",
  startDate: null,
  paymentDate: "THURSDAY",
};

// Parse a "YYYY-MM-DD" schedule date (or pass through a Date) into a
// local-midnight Date, matching how <input type="date"> reads/writes a day in
// local time. Building it from parts avoids the UTC-parse off-by-one that
// `new Date("2026-06-01")` would introduce.
export function toDateInputValue(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return value;
  const m = String(value)
    .slice(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m === null) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

// True when the `current` open schedule already has an invoice among
// `invoices`. In that case a material change (amount / frequency / payment day)
// supersedes on the server and only takes effect on next month's invoice, so
// the edit modal shows an informational note. A delivery-method-only change
// still applies immediately.
export function scheduleHasInvoice(current, invoices) {
  if (!current || !Array.isArray(invoices)) {
    return false;
  }
  return invoices.some((invoice) => invoice.scheduleId === current.id);
}

// Map a patient's current (open) schedule to the edit-form field values, so the
// modal opens showing exactly what is saved. Returns null when there is no
// current schedule — the create flow uses DEFAULT_SCHEDULE_FORM instead.
export function scheduleFormFromCurrent(current) {
  if (!current) {
    return null;
  }
  return {
    amountDollars: current.amountCents / 100,
    frequency: current.frequency,
    startDate: toDateInputValue(current.startDate),
    paymentDate: current.paymentDate,
  };
}
